import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Optional, Output } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { InputValidationResult } from '@bl/components/dist/input/types';
import { EmailInputProps } from '@bl/components/dist/types';
import { BLSvelteComponentWithValidation } from '@bl/components/dist/wrappers/wrappers';
import { distinctUntilChanged, Subscription } from 'rxjs';
import { BlFrontendBaseComponent } from './bl-frontend-base.component';

const generateUniqueNameId = (() => {
  let id = 0;
  return () => {
    id += 1;
    return `bl-frontend-email-input-name-${id}`;
  };
})();

@Component({
  selector: 'bl-frontend-email-input',
  template: ``,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlFrontendEmailInputComponent
  extends BlFrontendBaseComponent<EmailInputProps>
  implements ControlValueAccessor
{
  @Input() value: EmailInputProps['value'] = '';
  @Input() text: EmailInputProps['text'] = '';
  @Input() name: EmailInputProps['name'] = generateUniqueNameId();
  @Input() placeholder: EmailInputProps['placeholder'] = '';
  @Input() leftIcon: EmailInputProps['leftIcon'];
  @Input() rightIcon: EmailInputProps['rightIcon'];
  @Input() disabled: EmailInputProps['disabled'] = false;
  @Input() debounced: EmailInputProps['debounced'];
  @Input() description: EmailInputProps['description'] = '';
  @Input() clearable: EmailInputProps['clearable'];
  @Input() floatingText: EmailInputProps['floatingText'] = false;
  @Input() onValidate: EmailInputProps['onValidate'] = null;

  @Output() blurEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() keyDownEvent: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();
  @Output() valueChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() validationChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  private statusChangesSubscription: Subscription;
  constructor(
    protected override ref: ElementRef,
    @Optional() private ngControl: NgControl,
  ) {
    super(ref);
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  protected override onInitialized = (): void => {
    this.statusChangesSubscription = this.ngControl?.statusChanges?.pipe(distinctUntilChanged()).subscribe(() => {
      (this.instance as BLSvelteComponentWithValidation<EmailInputProps, unknown>).validate();
    });
  };

  protected override onDestroyed = (): void => {
    this.statusChangesSubscription?.unsubscribe();
  };

  private onChange: (value: unknown) => void;
  private onTouched: () => void;

  writeValue(value: EmailInputProps['value']): void {
    this.value = value;
    this.updateProps({ value });
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.updateProps({ disabled: isDisabled });
  }

  protected getComponentName(): string {
    return 'EmailInput';
  }

  private validateInput(value: string): InputValidationResult {
    if (!this.onValidate) {
      return { valid: true, message: '' };
    }

    return this.onValidate(value);
  }

  protected getProps(): EmailInputProps {
    const props: EmailInputProps = {
      value: this.value,
      debounced: this.debounced,
      description: this.description,
      disabled: this.disabled,
      name: this.name,
      placeholder: this.placeholder,
      text: this.text,
      floatingText: this.floatingText,
      clearable: this.clearable,
      leftIcon: this.leftIcon,
      rightIcon: this.rightIcon,

      onBlur: (value: string): void => {
        this.blurEvent.emit(value);
        this.onTouched?.();
      },
      onChange: (value: string): void => {
        this.value = value;
        this.valueChange.emit(value);
        this.onChange?.(value);
        const validationResult = this.validateInput(value);
        this.validationChange.emit(validationResult.valid);
      },
      onKeyDown: (e: KeyboardEvent): void => {
        this.keyDownEvent.emit(e);
      },
    };

    if (this.onValidate) {
      props.onValidate = this.validateInput.bind(this);
    }

    return props;
  }
}
